// The share dialog's half of Workspaces::SharingController. Every call returns
// the whole sharing state, so the dialog re-renders from the server's answer
// rather than reconciling a partial update against what it already had.

export type SharedPerson = {
  id: number
  email: string
  name: string | null
  role: "viewer" | "editor"
  // Shared with an address that has no verified account yet. They get in the
  // moment they have one; there is nothing for the owner to do.
  pending: boolean
}

export type SharingState = {
  workspace: { name: string }
  owner: { name: string; email: string }
  organization: {
    // The grant's id when shared, so revoking it is the same call as removing
    // a person. Null when the organization has no grant.
    id: number | null
    name: string
    shared: boolean
    role: "viewer" | "editor" | null
    member_count: number
  }
  people: SharedPerson[]
  link: { enabled: boolean; url: string | null }
}

function csrfToken(): string | undefined {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
}

function headers(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    "Accept": "application/json",
    "X-CSRF-Token": csrfToken() ?? "",
  }
}

// Every mutation answers with the full state or an error message, so they all
// go through one shape rather than each caller unpacking a different one.
export type SharingResult = { state: SharingState } | { error: string }

async function request(url: string, method: string, body?: unknown): Promise<SharingResult> {
  try {
    const response = await fetch(url, {
      method,
      headers: headers(),
      body: body === undefined ? undefined : JSON.stringify(body),
    })

    const payload = (await response.json()) as SharingState & { error?: string }
    if (!response.ok) return { error: payload.error ?? "Something went wrong." }
    return { state: payload }
  } catch {
    return { error: "Could not reach the server." }
  }
}

export function fetchSharing(baseUrl: string): Promise<SharingResult> {
  return request(baseUrl, "GET")
}

export function shareWithEmail(baseUrl: string, email: string, role: string): Promise<SharingResult> {
  return request(`${baseUrl}/grants`, "POST", { principal_type: "user", email, role })
}

export function shareWithOrganization(baseUrl: string, role: string): Promise<SharingResult> {
  return request(`${baseUrl}/grants`, "POST", { principal_type: "organization", role })
}

export function changeGrantRole(baseUrl: string, id: number, role: string): Promise<SharingResult> {
  return request(`${baseUrl}/grants/${id}`, "PATCH", { role })
}

export function revokeGrant(baseUrl: string, id: number): Promise<SharingResult> {
  return request(`${baseUrl}/grants/${id}`, "DELETE")
}

export function enableLink(baseUrl: string): Promise<SharingResult> {
  return request(`${baseUrl}/link`, "POST")
}

export function rotateLink(baseUrl: string): Promise<SharingResult> {
  return request(`${baseUrl}/link/rotate`, "POST")
}

export function disableLink(baseUrl: string): Promise<SharingResult> {
  return request(`${baseUrl}/link`, "DELETE")
}
